import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionPlansService } from '../services/plan.service';
import type { ConstructionPlan, PlanFilters, PlanItem } from '../types/construction-plan.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionPlans(companyId: string, initialFilters: PlanFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PlanFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-plans', companyId, filters],
    queryFn: () => ConstructionPlansService.getPlans(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-plan-stats', companyId, filters.projectId],
    queryFn: () => ConstructionPlansService.getPlanStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionPlan>) => ConstructionPlansService.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-plans'] });
      toast.success('Khởi tạo kế hoạch thi công thành công');
    },
  });

  return {
    plans: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createPlan: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionPlanDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-plans', 'detail', id],
    queryFn: () => ConstructionPlansService.getPlanDetail(id!),
    enabled: !!id,
  });

  const itemsQuery = useQuery({
    queryKey: ['construction-plans', 'items', id],
    queryFn: () => ConstructionPlansService.getPlanItems(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionPlan>) => ConstructionPlansService.updatePlan(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-plans'] });
      queryClient.invalidateQueries({ queryKey: ['construction-plans', 'detail', id] });
      toast.success('Cập nhật kế hoạch thành công');
    },
  });

  return {
    plan: query.data,
    items: itemsQuery.data || [],
    isLoading: query.isLoading || itemsQuery.isLoading,
    updatePlan: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
